import axios from 'axios';
import { API_BASE } from '../utils/apiBase.js';

const api = axios.create({
  // Relative '/api' on web (nginx proxy); absolute https://app.game-day.app/api
  // in the native Capacitor app (where '/api' would hit capacitor://localhost).
  baseURL: API_BASE,
  timeout: 15000,
  headers: { 'Content-Type': 'application/json' },
});

// ── Request: inject access token ─────────────────────────────────
// A request can opt out by setting `config.skipAuth = true` — used for
// public endpoints (e.g. the invite preview) so a stale/expired token
// from a prior session isn't attached, which would otherwise risk a 401
// that trips the response interceptor's logout+redirect on a page that
// is meant to work logged-out. (v2.5.176)
api.interceptors.request.use((config) => {
  if (config.skipAuth) {
    if (config.headers) delete config.headers.Authorization;
    return config;
  }
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// ── Response: refresh token with mutex ───────────────────────────
// If multiple requests fail with 401 simultaneously, only ONE refresh
// call is made. All other waiters queue behind the same promise and
// replay with the new token once it resolves.
let refreshPromise = null;

// May 6 2026 (v2.5.71) — rapid-refresh logout race fix.
// User report: F5 twice fast on the dashboard sometimes shows
// "None scheduled" / "No upcoming games", and a third F5 logs the
// user out entirely.
//
// Root cause: the refresh-token rotation flow had a tiny race window
// between "got new tokens from backend" and "wrote them to
// localStorage." Original code wrote the access token first, then
// the refresh token. If the user pressed F5 between those two lines
// (or during a GC pause / slow network), the new access token was
// persisted but the new refresh token wasn't — so on the next page
// load, localStorage held a STALE refresh token. The backend's
// theft-detection logic at /auth/refresh treats a previously-rotated
// refresh token as evidence of theft and revokes the entire token
// family → user gets logged out.
//
// Two fixes:
//
//   1. Reverse the write order: refresh token FIRST, then access.
//      This way, if F5 hits between writes, the next page load
//      sees the new refresh token (auth still works — it'll fail
//      the access token, hit /auth/refresh, which now uses the
//      ROTATED token correctly, and proceed normally). Worst case
//      now is an extra refresh-on-load, never a logout.
//
//   2. Keep a "previous_refresh_token" slot for ~10 seconds. If
//      the primary refresh attempt fails with TOKEN_THEFT_DETECTED
//      (or any 401), retry once with the previous slot. Covers the
//      window where two tabs / two rapid refreshes both attempt
//      a refresh and one wins. Without this, the loser logs out.
const PREV_REFRESH_KEY = 'refreshToken_prev';
const PREV_REFRESH_EXPIRY_KEY = 'refreshToken_prev_expiry';
// 2026-11-02: 8 s, not 10 s - the server's own window is 10 s from the
// moment IT rotated, which is earlier than the moment we saved the slot.
// Presenting the old token in the last stretch used to count as theft.
const PREV_REFRESH_GRACE_MS = 8_000;

const refreshAccessToken = async () => {
  const refreshToken = localStorage.getItem('refreshToken');
  if (!refreshToken) throw new Error('No refresh token');
  // SECURITY (Session 1, fix H-4): the backend now rotates refresh tokens
  // on every /refresh call — both `accessToken` and `refreshToken` come back
  // and the OLD refresh token is invalidated server-side. We MUST replace
  // both, otherwise the next /refresh attempt presents a rotated token,
  // which the backend treats as theft and revokes the entire token family.
  let data;
  // 2026-11-02: a refresh must not hang the whole app (every 401 waits on
  // it) but must also outlive a busy server - the 15 s default was the
  // "lost answer" that got whole sessions revoked (see routes/auth.js).
  const REFRESH_TIMEOUT_MS = 60_000;
  try {
    ({ data } = await axios.post('/api/auth/refresh', { refreshToken }, { timeout: REFRESH_TIMEOUT_MS }));
  } catch (err) {
    // May 6 2026 (v2.5.71) — REFRESH_RACE handling.
    // The backend returns 409 with code 'REFRESH_RACE' when the
    // presented token was just rotated within the last 10s (i.e.
    // another tab or another rapid-refresh cycle in this same
    // browser already used it successfully). The actual new tokens
    // landed in localStorage from that other refresh. We poll
    // localStorage briefly for a NEW token (different from the one
    // we just presented), then return it as if our refresh had
    // succeeded — the original failed request will retry with it.
    if (err?.response?.status === 409 && err.response?.data?.code === 'REFRESH_RACE') {
      // Poll up to 5x at 100ms intervals = 500ms total.
      for (let i = 0; i < 5; i++) {
        await new Promise(r => setTimeout(r, 100));
        const newRT = localStorage.getItem('refreshToken');
        if (newRT && newRT !== refreshToken) {
          // Another refresh in this browser succeeded and wrote new tokens.
          const newAT = localStorage.getItem('token');
          if (newAT) return newAT;
        }
      }
      // No other tab landed new tokens within 500ms — the race
      // resolved against us. Try one more refresh with whatever's
      // currently in storage; if it's still the same token, we'll
      // hit the same 409 and finally give up.
      const currentRT = localStorage.getItem('refreshToken');
      if (currentRT && currentRT !== refreshToken) {
        ({ data } = await axios.post('/api/auth/refresh', { refreshToken: currentRT }, { timeout: REFRESH_TIMEOUT_MS }));
      } else {
        throw err;
      }
    } else {
      // Pre-existing fallback: try the grace-window slot. (See
      // block-comment above for why this exists.)
      const prev = localStorage.getItem(PREV_REFRESH_KEY);
      const prevExpiry = parseInt(localStorage.getItem(PREV_REFRESH_EXPIRY_KEY) || '0', 10);
      if (prev && prev !== refreshToken && Date.now() < prevExpiry) {
        try {
          ({ data } = await axios.post('/api/auth/refresh', { refreshToken: prev }, { timeout: REFRESH_TIMEOUT_MS }));
          // Recovered! Fall through to the persistence block below.
        } catch {
          // Both stale — give up.
          localStorage.removeItem(PREV_REFRESH_KEY);
          localStorage.removeItem(PREV_REFRESH_EXPIRY_KEY);
          throw err;
        }
      } else {
        throw err;
      }
    }
  }
  // Persist the new refreshToken FIRST. If a page reload races us
  // between these two setItem calls, the next load needs the newer
  // refresh token to recover; an old access token will just trigger
  // a normal 401 → refresh cycle, which works.
  if (data.refreshToken) {
    // Move the about-to-be-replaced token into the grace slot before
    // overwriting it. Anyone mid-flight using the old token has
    // ~10s to recover.
    localStorage.setItem(PREV_REFRESH_KEY, refreshToken);
    localStorage.setItem(PREV_REFRESH_EXPIRY_KEY, String(Date.now() + PREV_REFRESH_GRACE_MS));
    localStorage.setItem('refreshToken', data.refreshToken);
  }
  localStorage.setItem('token', data.accessToken);
  return data.accessToken;
};

// 2026-11-02: ONE refresh at a time for the whole tab. The chat socket used
// to run its own /auth/refresh beside the interceptor's - two refreshes
// with the same token in the same second, and a failure in the socket's
// copy wiped the session outright. Everyone now shares this promise.
export const refreshSession = () => {
  if (!refreshPromise) {
    refreshPromise = refreshAccessToken().finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
};

// 2026-11-02: another tab logged out (or a logout wiped the tokens) and
// this tab kept polling - with no token the server says "No token
// provided", the refresh has nothing to work with, and the page used to
// show that text. Send the tab to the login page instead.
export const hasSession = () => !!localStorage.getItem('refreshToken');

api.interceptors.response.use(
  (res) => res,
  async (error) => {
    const original = error.config;

    // 2026-07-12 (coach report: rapid team switches → blank pages) —
    // GLOBAL one-shot retry for rate-limited GETs. Full-reload team
    // switches fire bursts of boot queries; the few that catch a 429
    // used to fail permanently, leaving half-rendered/blank pages that
    // only a manual refresh fixed. GETs are idempotent, so retry each
    // exactly once after a short backoff (honoring Retry-After when the
    // server sends one, capped at 5s). Non-GETs are never auto-retried
    // (a rate-limited POST must stay the caller's decision).
    if (
      error.response?.status === 429 &&
      original &&
      (original.method || 'get').toLowerCase() === 'get' &&
      !original._retried429
    ) {
      original._retried429 = true;
      const ra = Number(error.response.headers?.['retry-after']);
      const delayMs = Number.isFinite(ra) && ra > 0 ? Math.min(ra * 1000, 5000) : 1500;
      await new Promise(r => setTimeout(r, delayMs));
      return api(original);
    }

    // v2.5.130 — broaden the retry condition.
    //
    // Previously: only TOKEN_EXPIRED 401s triggered a silent refresh.
    // Other 401s ("Invalid token", "No token provided", "User not
    // found") fell through to the page handlers, which uniformly
    // mis-rendered the empty state as "League not found" (or
    // equivalent on team-side pages) when the user was actually
    // just logged out.
    //
    // The new logic: try a refresh on ANY 401 (provided the request
    // wasn't itself an auth endpoint and hasn't already been retried).
    // If refresh succeeds → original request retries with the new
    // token. If refresh fails → user is genuinely logged out and we
    // redirect to /login as before.
    //
    // Three exclusions:
    //   1. Don't retry /auth/refresh or /auth/login — that path is
    //      its own concern; retrying it would loop forever.
    //   2. Don't retry if already retried (the _retry flag).
    //   3. Skip non-401 errors (only 401s are auth-related).
    const isAuthPath = original?.url?.includes('/auth/refresh')
                    || original?.url?.includes('/auth/login');
    if (
      error.response?.status === 401 &&
      !isAuthPath &&
      !original._retry &&
      !original.skipAuth   // v2.5.176 — public calls never trigger logout/redirect
    ) {
      original._retry = true;

      if (!hasSession()) {
        // only a request that WENT OUT with a token has lost a session; a
        // logged-out visitor's call to a protected endpoint is just refused
        const hadToken = !!(original.headers?.Authorization || original.headers?.authorization);
        if (hadToken && !original.skipAuthRedirect && !window.location.pathname.startsWith('/login')) {
          localStorage.removeItem('token');
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }

      try {
        // If a refresh is already in flight, wait for it — don't start a second one
        const newToken = await refreshSession();
        original.headers.Authorization = `Bearer ${newToken}`;
        return api(original);
      } catch (refreshErr) {
        // 2026-07-12 (coach report: fast team-switching logs you out) —
        // DON'T treat a TRANSIENT refresh failure as a dead session.
        // Every sidebar team switch is a full page reload (by design,
        // v2.5.2xx) and each boot fires ~20+ API calls; clicking several
        // teams quickly can 429 the burst allowance — INCLUDING
        // /auth/refresh itself (it sits behind authLimiter). The old
        // catch wiped both tokens and hard-redirected to /login on ANY
        // refresh failure, so a rate-limited refresh == forced logout.
        // Now: rate-limit (429), server errors (5xx) and network drops
        // (no response at all) just reject the original request — the
        // session is intact and the next attempt refreshes normally.
        // Only a definitive auth verdict (401/403 from the refresh
        // endpoint = refresh token truly invalid/expired) wipes + redirects.
        const refreshStatus = refreshErr?.response?.status;
        const transient = refreshStatus === 429 || refreshStatus >= 500 || !refreshStatus;
        if (transient) {
          refreshPromise = null;
          // callers that decide whether to drop the session (AuthContext's
          // boot probe) must know this 401 was never settled
          error.refreshTransient = true;
          return Promise.reject(error);
        }
        // Refresh failed — clear credentials and redirect to login.
        // v2.5.130 — avoid the redirect loop if we're already on
        // /login: replacing the URL would trigger a fresh full
        // page load and re-fire any background API calls (e.g.
        // the auth check on the login page itself), causing a
        // cascade of 401s and an apparent freeze.
        refreshPromise = null;
        // v2.5.223 — the bootstrap session check (AuthContext.loadUser)
        // opts out of the hard wipe + redirect via `skipAuthRedirect`. At
        // app load we may be racing a fresh login — specifically the email
        // "I'm In" magic-login on /rsvp/result. The browser's STALE token
        // 401s here; without this guard we'd wipe the freshly-stored magic
        // session and hard-redirect to /login. That is the reported "first
        // click marks me playing but doesn't log me in; the second click
        // does" bug: the first click nuked the stale token (and the
        // race-stored fresh one), so the second click started clean and
        // worked. For the bootstrap check we let loadUser clean up
        // gracefully (React-Router redirect) instead of a hard nav.
        if (!original.skipAuthRedirect) {
          localStorage.removeItem('token');
          localStorage.removeItem('refreshToken');
          localStorage.removeItem(PREV_REFRESH_KEY);
          localStorage.removeItem(PREV_REFRESH_EXPIRY_KEY);
          if (!window.location.pathname.startsWith('/login')) {
            window.location.href = '/login';
          }
        }
        return Promise.reject(error);
      }
    }

    return Promise.reject(error);
  }
);

export default api;
